/**
 * SegmentEditor — F12.I E2.T4.
 *
 * Lista segmentów wzoru numeracji + Add popover (9 typów) + delete per segment
 * + DnD reorder (@dnd-kit/sortable).
 *
 * Każdy segment renderuje swój SegmentForm inline (edycja parametrów per typ).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';

import {
  defaultSegmentForType,
  type NumberingSegment,
  SEGMENT_TYPES,
} from '@/lib/numbering-preview';

import { SegmentForm } from './SegmentForm';

interface ModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
}

interface Props {
  segments: NumberingSegment[];
  onChange: (next: NumberingSegment[]) => void;
  /** Pola modułu — używane przez field_value / conditional_text form. */
  fields?: ModuleField[];
  /** Prefix do data-testid (np. slug kategorii). */
  testIdPrefix: string;
}

// ─── Sortable item ───────────────────────────────────────────────────────────

interface SortableItemProps {
  id: string;
  segment: NumberingSegment;
  onUpdate: (next: NumberingSegment) => void;
  onRemove: () => void;
  fields: ModuleField[];
  testIdPrefix: string;
}

function SortableSegmentItem({
  id,
  segment,
  onUpdate,
  onRemove,
  fields,
  testIdPrefix,
}: SortableItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2"
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={t('tenant.numbering.segments.dragHandle')}
          className="mt-1 shrink-0 cursor-grab text-text-muted hover:text-text-primary active:cursor-grabbing"
          data-testid={`${testIdPrefix}-drag`}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Type label */}
        <span className="mt-0.5 inline-flex h-5 shrink-0 items-center rounded bg-bg-tertiary px-1.5 text-xs font-medium text-text-secondary">
          {t(`tenant.numbering.segments.typeLabel.${segment.type}`)}
        </span>

        {/* Inline form */}
        <div className="min-w-0 flex-1">
          <SegmentForm
            segment={segment}
            onChange={onUpdate}
            fields={fields}
            testIdPrefix={testIdPrefix}
          />
        </div>

        {/* Delete */}
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('tenant.numbering.segments.delete')}
          className="shrink-0 text-text-muted hover:text-error-500"
          data-testid={`${testIdPrefix}-delete`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

// ─── Add popover ─────────────────────────────────────────────────────────────

interface AddPopoverProps {
  onAdd: (segment: NumberingSegment) => void;
  testIdPrefix: string;
}

function AddSegmentPopover({ onAdd, testIdPrefix }: AddPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handlePick = (type: NumberingSegment['type']) => {
    onAdd(defaultSegmentForType(type));
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary"
        data-testid={`${testIdPrefix}-add-segment`}
      >
        <Plus className="h-4 w-4" />
        {t('tenant.numbering.segments.add')}
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <button
            type="button"
            aria-hidden="true"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default bg-transparent"
          />
          {/* Popover panel */}
          <div className="absolute left-0 z-20 mt-1 w-64 rounded-md border border-border-primary bg-bg-primary p-1 shadow-lg">
            <p className="px-2 py-1.5 text-xs font-medium text-text-muted">
              {t('tenant.numbering.segments.addTypeTitle')}
            </p>
            {SEGMENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handlePick(type)}
                className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-text-primary transition-colors hover:bg-bg-secondary"
                data-testid={`${testIdPrefix}-add-segment-${type}`}
              >
                {t(`tenant.numbering.segments.typeLabel.${type}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main editor ─────────────────────────────────────────────────────────────

export function SegmentEditor({ segments, onChange, fields, testIdPrefix }: Props) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // DnD wymaga stabilnych ID per item. Segmenty są ułożone w array bez ID —
  // generujemy syntetyczny ID na bazie indexu. Po reorderze indeksy się zmienią,
  // ale to ok bo arrayMove zwraca świeży array. ID są kompozycją typu + idx
  // dla stabilności w obrębie jednego renderu.
  const items = segments.map((s, idx) => ({ id: `${idx}-${s.type}`, segment: s, idx }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(segments, oldIndex, newIndex));
  };

  const addSegment = (segment: NumberingSegment) => {
    onChange([...segments, segment]);
  };

  const updateSegment = (index: number, next: NumberingSegment) => {
    onChange(segments.map((s, i) => (i === index ? next : s)));
  };

  const removeSegment = (index: number) => {
    onChange(segments.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {segments.length === 0 ? (
        <div className="rounded-md border border-dashed border-border-primary px-3 py-4 text-center text-xs text-text-muted">
          {t('tenant.numbering.segments.empty')}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2" data-testid={`${testIdPrefix}-segments`}>
              {items.map((item) => (
                <SortableSegmentItem
                  key={item.id}
                  id={item.id}
                  segment={item.segment}
                  onUpdate={(next) => updateSegment(item.idx, next)}
                  onRemove={() => removeSegment(item.idx)}
                  fields={fields ?? []}
                  testIdPrefix={`${testIdPrefix}-seg-${item.idx}`}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <AddSegmentPopover onAdd={addSegment} testIdPrefix={testIdPrefix} />
    </div>
  );
}
